import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  section: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  addressCard: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    position: "relative",
  },
  removeAddressBtn: { position: "absolute", top: 4, right: 4 },
  tagChip: { margin: 2, color: "#fff" },
}));

const emptyAddress = {
  label: "",
  zipCode: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  country: "BR",
  isPrimary: false,
};

const initialState = {
  personType: "PF",
  firstName: "",
  lastName: "",
  cpf: "",
  birthDate: "",
  companyName: "",
  tradeName: "",
  cnpj: "",
  openingDate: "",
  stateRegistration: "",
  municipalRegistration: "",
  email: "",
  phone: "",
  mobilePhone: "",
  website: "",
  notes: "",
  statusId: "",
  sourceId: "",
  tagIds: [],
  addresses: [],
  extraInfo: [],
  // Objeto, nao array — chave e o id da definicao. Formato mais simples de
  // preencher no formulario: cada campo renderiza olhando o proprio id,
  // sem depender da ordem em que os campos definidos e o cliente chegaram
  // da API (sao duas chamadas paralelas).
  customFieldValues: {},
};

const nz = (v) => (v === null || v === undefined ? "" : v);

const ClientSchema = Yup.object().shape({
  personType: Yup.string().oneOf(["PF", "PJ"]).required(),
  firstName: Yup.string().when("personType", {
    is: "PF",
    then: Yup.string().required("Obrigatório para PF"),
  }),
  companyName: Yup.string().when("personType", {
    is: "PJ",
    then: Yup.string().required("Obrigatório para PJ"),
  }),
  email: Yup.string().email("E-mail inválido").nullable(),
});

const ClientModal = ({ open, onClose, clientId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [client, setClient] = useState(initialState);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [s, src, tg, fd] = await Promise.all([
          api.get("/client-statuses"),
          api.get("/client-sources"),
          api.get("/client-tags"),
          api.get("/client-field-definitions"),
        ]);
        if (!isMounted.current) return;
        setStatuses(s.data);
        setSources(src.data);
        setTags(tg.data);
        // Só os ativos entram no formulário. Um campo desativado continua
        // com o valor gravado no cliente — só para de aparecer para edição.
        setFieldDefinitions((fd.data || []).filter((f) => f.active));
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!clientId) {
      setClient(initialState);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        if (!isMounted.current) return;
        const normalized = {
          ...initialState,
          personType: data.personType || "PF",
          firstName: nz(data.firstName),
          lastName: nz(data.lastName),
          cpf: nz(data.cpf),
          birthDate: data.birthDate ? String(data.birthDate).slice(0, 10) : "",
          companyName: nz(data.companyName),
          tradeName: nz(data.tradeName),
          cnpj: nz(data.cnpj),
          openingDate: data.openingDate ? String(data.openingDate).slice(0, 10) : "",
          stateRegistration: nz(data.stateRegistration),
          municipalRegistration: nz(data.municipalRegistration),
          email: nz(data.email),
          phone: nz(data.phone),
          mobilePhone: nz(data.mobilePhone),
          website: nz(data.website),
          notes: nz(data.notes),
          statusId: data.statusId || "",
          sourceId: data.sourceId || "",
          tagIds: (data.tags || []).map((t) => t.id),
          addresses: (data.addresses || []).map((a) => ({
            ...emptyAddress,
            id: a.id,
            label: nz(a.label),
            zipCode: nz(a.zipCode),
            street: nz(a.street),
            number: nz(a.number),
            complement: nz(a.complement),
            neighborhood: nz(a.neighborhood),
            city: nz(a.city),
            state: nz(a.state),
            country: a.country || "BR",
            isPrimary: !!a.isPrimary,
          })),
          extraInfo: (data.extraInfo || []).map((e) => ({
            id: e.id,
            name: nz(e.name),
            value: nz(e.value),
          })),
          customFieldValues: (data.customFieldValues || []).reduce((acc, v) => {
            acc[v.definitionId] = nz(v.value);
            return acc;
          }, {}),
        };
        setClient(normalized);
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, open]);

  const handleClose = () => {
    setClient(initialState);
    onClose();
  };

  const handleSave = async (values) => {
    // Obrigatoriedade so e cobrada aqui, no formulario — o backend aceita
    // gravar sem isso. Um campo virar obrigatorio depois de clientes ja
    // existirem, ou um cliente entrando pelo formulario publico (que nao
    // passa por esta tela), nao pode travar em nenhum dos dois casos.
    const faltando = fieldDefinitions.filter(
      (f) => f.required && !String(values.customFieldValues[f.id] ?? "").trim()
    );
    if (faltando.length > 0) {
      toast.error(`Preencha: ${faltando.map((f) => f.label).join(", ")}`);
      return;
    }

    const payload = {
      ...values,
      statusId: values.statusId || null,
      sourceId: values.sourceId || null,
      birthDate: values.birthDate || null,
      openingDate: values.openingDate || null,
      customFieldValues: Object.entries(values.customFieldValues || {}).map(
        ([definitionId, value]) => ({ definitionId: Number(definitionId), value })
      ),
    };
    try {
      let saved;
      if (clientId) {
        const { data } = await api.put(`/clients/${clientId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/clients", payload);
        saved = data;
      }
      toast.success("Cliente salvo");
      if (onSave) onSave(saved);
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{clientId ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
      <Formik
        initialValues={client}
        enableReinitialize
        validationSchema={ClientSchema}
        onSubmit={(values, actions) => {
          handleSave(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Typography variant="subtitle1" className={classes.section}>
                Tipo de pessoa
              </Typography>
              <RadioGroup
                row
                name="personType"
                value={values.personType}
                onChange={(e) => setFieldValue("personType", e.target.value)}
              >
                <FormControlLabel value="PF" control={<Radio />} label="Pessoa Física" />
                <FormControlLabel value="PJ" control={<Radio />} label="Pessoa Jurídica" />
              </RadioGroup>

              {values.personType === "PF" ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Nome *"
                      name="firstName"
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="Sobrenome" name="lastName" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="CPF" name="cpf" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Data de nascimento"
                      name="birthDate"
                      type="date"
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Razão Social *"
                      name="companyName"
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="Nome Fantasia" name="tradeName" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="CNPJ" name="cnpj" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Data de abertura"
                      name="openingDate"
                      type="date"
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="Inscrição Estadual" name="stateRegistration" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field as={TextField} label="Inscrição Municipal" name="municipalRegistration" fullWidth margin="dense" variant="outlined" />
                  </Grid>
                </Grid>
              )}

              <Typography variant="subtitle1" className={classes.section}>Contato</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field as={TextField} label="E-mail" name="email" fullWidth margin="dense" variant="outlined"
                    error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field as={TextField} label="Website" name="website" fullWidth margin="dense" variant="outlined" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field as={TextField} label="Telefone" name="phone" fullWidth margin="dense" variant="outlined" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field as={TextField} label="Celular" name="mobilePhone" fullWidth margin="dense" variant="outlined" />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" className={classes.section}>Classificação</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    select
                    label="Status"
                    name="statusId"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                  >
                    <MenuItem value="">— nenhum —</MenuItem>
                    {statuses.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    select
                    label="Origem"
                    name="sourceId"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                  >
                    <MenuItem value="">— nenhuma —</MenuItem>
                    {sources.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Tags</Typography>
                  <div>
                    {tags.map((t) => {
                      const selected = values.tagIds.includes(t.id);
                      return (
                        <Chip
                          key={t.id}
                          label={t.name}
                          size="small"
                          className={classes.tagChip}
                          style={{ backgroundColor: selected ? t.color : "#bbb" }}
                          onClick={() => {
                            const next = selected
                              ? values.tagIds.filter((x) => x !== t.id)
                              : [...values.tagIds, t.id];
                            setFieldValue("tagIds", next);
                          }}
                        />
                      );
                    })}
                  </div>
                </Grid>
              </Grid>

              {fieldDefinitions.length > 0 && (
                <>
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="subtitle1" className={classes.section}>
                    Campos personalizados
                  </Typography>
                  <Grid container spacing={2}>
                    {fieldDefinitions.map((f) => {
                      const valorAtual = values.customFieldValues[f.id] ?? "";
                      const definir = (v) => setFieldValue(`customFieldValues.${f.id}`, v);

                      if (f.type === "boolean") {
                        return (
                          <Grid item xs={12} sm={4} key={f.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={valorAtual === "true" || valorAtual === true}
                                  onChange={(e) => definir(e.target.checked ? "true" : "false")}
                                />
                              }
                              label={f.label}
                            />
                          </Grid>
                        );
                      }

                      if (f.type === "select") {
                        return (
                          <Grid item xs={12} sm={4} key={f.id}>
                            <TextField
                              select
                              label={f.required ? `${f.label} *` : f.label}
                              value={valorAtual}
                              onChange={(e) => definir(e.target.value)}
                              fullWidth
                              margin="dense"
                              variant="outlined"
                            >
                              <MenuItem value="">— nenhum —</MenuItem>
                              {(f.options || []).map((op) => (
                                <MenuItem key={op} value={op}>{op}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        );
                      }

                      return (
                        <Grid item xs={12} sm={4} key={f.id}>
                          <TextField
                            label={f.required ? `${f.label} *` : f.label}
                            type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                            value={valorAtual}
                            onChange={(e) => definir(e.target.value)}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}

              <Divider style={{ margin: "16px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1">Endereços</Typography>
                <FieldArray name="addresses">
                  {({ push }) => (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => push({ ...emptyAddress })}
                    >
                      Adicionar endereço
                    </Button>
                  )}
                </FieldArray>
              </div>

              <FieldArray name="addresses">
                {({ remove }) => (
                  <>
                    {values.addresses.map((addr, i) => (
                      <div key={i} className={classes.addressCard}>
                        <IconButton size="small" className={classes.removeAddressBtn} onClick={() => remove(i)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <Field as={TextField} label="Rótulo" name={`addresses.${i}.label`} fullWidth margin="dense" variant="outlined" placeholder="Cobrança / Entrega" />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Field as={TextField} label="CEP" name={`addresses.${i}.zipCode`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={12} sm={5}>
                            <Field as={TextField} label="Logradouro" name={`addresses.${i}.street`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Field as={TextField} label="Nº" name={`addresses.${i}.number`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={8} sm={4}>
                            <Field as={TextField} label="Complemento" name={`addresses.${i}.complement`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field as={TextField} label="Bairro" name={`addresses.${i}.neighborhood`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={8} sm={3}>
                            <Field as={TextField} label="Cidade" name={`addresses.${i}.city`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={4} sm={1}>
                            <Field as={TextField} label="UF" name={`addresses.${i}.state`} fullWidth margin="dense" variant="outlined" inputProps={{ maxLength: 2 }} />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Field as={TextField} label="País" name={`addresses.${i}.country`} fullWidth margin="dense" variant="outlined" />
                          </Grid>
                          <Grid item xs={6} sm={2} style={{ display: "flex", alignItems: "center" }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!values.addresses[i].isPrimary}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      values.addresses.forEach((_, j) => {
                                        setFieldValue(`addresses.${j}.isPrimary`, j === i);
                                      });
                                    } else {
                                      setFieldValue(`addresses.${i}.isPrimary`, false);
                                    }
                                  }}
                                />
                              }
                              label="Principal"
                            />
                          </Grid>
                        </Grid>
                      </div>
                    ))}
                  </>
                )}
              </FieldArray>

              <Typography variant="subtitle1" className={classes.section}>Observações</Typography>
              <Field
                as={TextField}
                name="notes"
                fullWidth
                multiline
                minRows={3}
                margin="dense"
                variant="outlined"
                placeholder="Notas internas..."
              />

              <Divider style={{ margin: "16px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1">Informações adicionais</Typography>
                <FieldArray name="extraInfo">
                  {({ push }) => (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => push({ name: "", value: "" })}
                    >
                      Adicionar campo
                    </Button>
                  )}
                </FieldArray>
              </div>
              <FieldArray name="extraInfo">
                {({ remove }) => (
                  <>
                    {values.extraInfo &&
                      values.extraInfo.map((info, index) => (
                        <Grid container spacing={1} key={`extra-${index}`} alignItems="center">
                          <Grid item xs={5}>
                            <Field
                              as={TextField}
                              label="Nome"
                              name={`extraInfo.${index}.name`}
                              fullWidth
                              margin="dense"
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Field
                              as={TextField}
                              label="Valor"
                              name={`extraInfo.${index}.value`}
                              fullWidth
                              margin="dense"
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton size="small" onClick={() => remove(index)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                  </>
                )}
              </FieldArray>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {clientId ? "Salvar" : "Criar"}
                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ClientModal;
